// 真實 7 日天氣預報：Nominatim 將城市名轉座標，再向 Open-Meteo 取預報。
// 兩者都免費、免 API key，取代舊有由 AI 估算的「本季典型天氣」。

const COORD_PFX = 'ec_wxgeo_';
const WX_PFX = 'ec_wx3_';
const COORD_TTL = 90 * 24 * 60 * 60 * 1000; // 城市座標幾乎不變
const WX_TTL = 3 * 60 * 60 * 1000;          // 預報每幾小時更新一次

const readCache = key => {
  try {
    const item = JSON.parse(localStorage.getItem(key));
    if (!item || Date.now() > item.exp) return null;
    return item.data;
  } catch { return null; }
};
const writeCache = (key, data, ttl) => {
  try { localStorage.setItem(key, JSON.stringify({ data, exp: Date.now() + ttl })); } catch {}
};

async function geocodeCity(city, country) {
  const q = [city, country].filter(Boolean).join(', ');
  const key = COORD_PFX + q;
  const cached = readCache(key);
  if (cached) return cached;
  const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`);
  if (!res.ok) return null;
  const hit = (await res.json())?.[0];
  if (!hit) return null;
  const coords = { lat: parseFloat(hit.lat), lng: parseFloat(hit.lon) };
  writeCache(key, coords, COORD_TTL);
  return coords;
}

// WMO 天氣代碼 → WeatherStrip 的圖示。
// Open-Meteo 的 weather_code 只要當天「任何時段」出現該現象就標記整天，颱風／熱帶系統
// 影響期間常見同一天 code=雨、precipitation_probability_max 卻只有 6-7 成，導致大致晴天
// 偶有驟雨的日子被畫成整天下雨（2026-09-28 實測：天文台預報香港「大致天晴、局部驟雨、
// 降雨概率低」，Open-Meteo 卻連 7 天全標雨天圖示）。加機率門檻：低於 50% 不算「雨天」，
// 只在真的較可能下雨時才畫雨/雪圖示。
function wmoToIcon(code, windMax, precipProb) {
  const likely = precipProb == null || precipProb >= 50;
  if ([71, 73, 75, 77, 85, 86].includes(code) && likely) return 'snowy';
  if (((code >= 51 && code <= 67) || (code >= 80 && code <= 82) || code >= 95) && likely) return 'rainy';
  if (windMax >= 40) return 'windy';
  if (code <= 1) return 'sunny';
  return 'cloudy';
}

// 回傳 [{ day, high, low, icon }]，失敗時回傳空陣列（天氣條會自動隱藏）
export async function fetchRealWeather(city, country, lang = 'en') {
  if (!city) return [];
  try {
    const coords = await geocodeCity(city, country);
    if (!coords) return [];
    const key = `${WX_PFX}${coords.lat.toFixed(2)}_${coords.lng.toFixed(2)}`;
    let daily = readCache(key);
    if (!daily) {
      // models=icon_seamless：預設 best_match 在熱帶氣旋影響期間常選到過度估計降雨的模式
      // （2026-09-28 實測：香港天文台預報「大致天晴、局部驟雨、機率低」，best_match 卻回報
      // 60-99% 降雨機率、連續整週雨天圖示）；同一組座標比對下，DWD ICON 模式明顯較貼近官方
      // 預報（機率隨時間漸升，非一開始就全週高機率）。仍是同一支免費 API，只換模式參數。
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lng}`
        + '&daily=weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max,precipitation_probability_max'
        + '&timezone=auto&forecast_days=7&models=icon_seamless';
      const res = await fetch(url);
      if (!res.ok) return [];
      daily = (await res.json())?.daily;
      if (!daily?.time?.length) return [];
      writeCache(key, daily, WX_TTL);
    }
    let fmt;
    try { fmt = new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' }); }
    catch { fmt = new Intl.DateTimeFormat('en', { weekday: 'short', timeZone: 'UTC' }); }
    return daily.time.map((date, i) => ({
      // 以 UTC 正午解析，避免用戶時區令星期錯位
      day: fmt.format(new Date(`${date}T12:00:00Z`)),
      high: daily.temperature_2m_max[i] != null ? Math.round(daily.temperature_2m_max[i]) : null,
      low: daily.temperature_2m_min[i] != null ? Math.round(daily.temperature_2m_min[i]) : null,
      icon: wmoToIcon(daily.weather_code[i], daily.wind_speed_10m_max[i] ?? 0, daily.precipitation_probability_max?.[i]),
    }));
  } catch {
    return [];
  }
}
